import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const element = document.getElementById('cloud-auto-gift-wrap-config');
if (element && !window.cloudAutoGiftWrap) {
  const config = JSON.parse(element.textContent);
  const root = window.Shopify.routes.root;
  const marker = '_cloud_auto_gift_wrap';
  const storageKey = 'cloud-auto-gift-wrap-v1';
  let state = {};
  try { state = JSON.parse(sessionStorage.getItem(storageKey) || '{}'); } catch {}
  let running;
  let pending = false;
  let publishing = false;
  let bypassCheckout = false;
  let changed = false;
  let sections = {};

  const save = () => {
    try { sessionStorage.setItem(storageKey, JSON.stringify(state)); } catch {}
  };
  const isWrap = item => Number(item.variant_id) === config.variantId;
  const isAuto = item => isWrap(item) && item.properties?.[marker] === '1';
  const isFree = item => item.quantity === 1 && item.final_line_price === 0 &&
    (item.line_level_discount_allocations || []).some(allocation =>
      allocation.amount > 0 && allocation.discount_application?.title === config.discountTitle
    );
  const eligibleAmount = cart => cart.items.reduce((total, item) => {
    const excluded = isWrap(item) || item.product_title === 'Gift Wrap' ||
      item.product_title === 'Gift Voucher' || item.gift_card || item.original_price <= 0;
    const productDiscount = (item.line_level_discount_allocations || [])
      .some(allocation => allocation.amount > 0);
    return total + (excluded || productDiscount ? 0 : item.original_line_price);
  }, 0);
  const fingerprint = cart => JSON.stringify([
    cart.currency,
    cart.items.filter(item => !isWrap(item)).map(item => [
      item.key, item.quantity, item.original_line_price, item.line_level_discount_allocations
    ]),
    cart.cart_level_discount_applications
  ]);
  const request = async (path, body) => {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (!response.ok) throw new Error('Cart request failed: ' + response.status);
    return response.json();
  };
  const readCart = () => request('cart.js');
  const mutate = async (path, body) => {
    const ids = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map(component => component.dataset.sectionId))].slice(0, 5);
    changed = true;
    const response = await request(path, {
      ...body,
      ...(ids.length ? { sections: ids, sections_url: window.location.pathname } : {})
    });
    sections = response.sections || {};
    return readCart();
  };
  const remove = item => mutate('cart/change.js', { id: item.key, quantity: 0 });
  const publish = cart => {
    if (!changed) return;
    publishing = true;
    document.dispatchEvent(new CartUpdateEvent(cart, 'cloud-auto-gift-wrap', {
      itemCount: cart.item_count,
      source: 'cloud-auto-gift-wrap',
      sections
    }));
    publishing = false;
    changed = false;
    sections = {};
  };
  const reconcile = async () => {
    let cart = await readCart();
    if (state.token !== cart.token) state = { token: cart.token };
    const eligible = false;
    const signature = fingerprint(cart);
    let auto = cart.items.filter(isAuto);
    if (!eligible) {
      state.optOut = false;
      state.denied = null;
    } else if (state.hadAuto && !auto.length) {
      state.optOut = true;
    }
    save();
    const manual = cart.items.some(item => isWrap(item) && !isAuto(item));
    if (!eligible || manual) {
      for (let attempt = 0; attempt < 5; attempt++) {
        const item = cart.items.find(isAuto);
        if (!item) break;
        cart = await remove(item);
      }
    } else {
      for (let attempt = 0; attempt < 5; attempt++) {
        auto = cart.items.filter(isAuto);
        if (auto.length <= 1 && (!auto.length || auto[0].quantity === 1)) break;
        cart = await mutate('cart/change.js', {
          id: auto[auto.length - 1].key,
          quantity: auto.length > 1 ? 0 : 1
        });
      }
      if (!cart.items.some(isAuto) && !state.optOut && state.denied !== signature) {
        state.denied = signature;
        save();
        cart = await mutate('cart/add.js', {
          items: [{ id: config.variantId, quantity: 1, properties: { [marker]: '1' } }]
        });
      }
      for (let attempt = 0; attempt < 5; attempt++) {
        const item = cart.items.find(item => isAuto(item) && !isFree(item));
        if (!item) break;
        state.denied = signature;
        save();
        cart = await remove(item);
      }
      if (cart.items.some(isAuto)) state.denied = null;
    }
    if (cart.items.filter(isAuto).length > 1 || cart.items.some(item => isAuto(item) && !isFree(item))) {
      throw new Error('Automatic gift wrap needs to be removed');
    }
    state.hadAuto = cart.items.some(isAuto);
    save();
    publish(cart);
    return cart;
  };
  const schedule = () => {
    pending = true;
    if (running) return running;
    running = (async () => {
      try {
        let cart;
        while (pending) {
          pending = false;
          cart = await reconcile();
        }
        return cart;
      } catch (error) {
        try {
          let cart = await readCart();
          for (let attempt = 0; attempt < 5; attempt++) {
            const item = cart.items.find(item => isAuto(item) && !isFree(item));
            if (!item) break;
            cart = await remove(item);
          }
          state.hadAuto = cart.items.some(isAuto);
          save();
          publish(cart);
        } catch {}
        throw error;
      } finally {
        running = null;
      }
    })();
    return running;
  };
  const onChange = event => {
    if (publishing || event?.detail?.sourceId === 'cloud-auto-gift-wrap') return;
    schedule().catch(error => console.error('Cloud gift wrap:', error));
  };
  const checkCheckout = async resume => {
    try {
      await schedule();
      bypassCheckout = true;
      try { resume(); } finally { bypassCheckout = false; }
    } catch {
      window.alert(config.errorMessage);
    }
  };
  document.addEventListener('click', event => {
    if (bypassCheckout) return;
    const target = event.target instanceof Element ? event.target.closest('a[href], button, input[type="submit"]') : null;
    if (!target) return;
    const url = target.matches('a[href]') ? new URL(target.href, window.location.href) : null;
    const link = url && url.origin === window.location.origin && /\/checkout(?:\/|$)/.test(url.pathname);
    if (!link && target.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const targetId = target.id;
    checkCheckout(() => {
      if (link) return window.location.assign(url.href);
      const button = target.isConnected ? target :
        (targetId ? document.getElementById(targetId) : null) || document.querySelector('[name="checkout"]');
      if (button) button.click();
      else window.location.assign(root + 'checkout');
    });
  }, true);
  document.addEventListener('submit', event => {
    if (bypassCheckout) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    const formId = form.id;
    checkCheckout(() => {
      const currentForm = form.isConnected ? form : (formId ? document.getElementById(formId) : null);
      if (!currentForm) return window.location.assign(root + 'checkout');
      const button = submitter?.isConnected ? submitter : currentForm.querySelector('[name="checkout"]');
      if (button) currentForm.requestSubmit(button);
      else window.location.assign(root + 'checkout');
    });
  }, true);
  document.addEventListener(ThemeEvents.cartUpdate, onChange);
  document.addEventListener(ThemeEvents.discountUpdate, onChange);
  document.addEventListener('shopify:section:load', onChange);
  window.addEventListener('pageshow', onChange);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) onChange(); });
  window.cloudAutoGiftWrap = { sync: schedule };
  onChange();
}